import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { LogOut, Settings, HelpCircle } from 'lucide-react'
import { athleteMenuItems } from './athleteNavigation'
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu'
import { sports } from '@/config'

interface UserMenuContentProps {
  showUserInfo?: boolean
}

export const UserMenuContent = ({ showUserInfo = false }: UserMenuContentProps) => {
  const navigate = useNavigate()
  const { athlete, logout } = useAuth()

  const handleLogout = () => {
    logout()
    navigate('/signin')
  }

  // Helper to get sport display name(s) - copied/adapted from Sidebar.tsx
  const getSportDisplay = () => {
    if (!athlete?.sports || (Array.isArray(athlete.sports) && athlete.sports.length === 0)) {
      return 'No sport'
    }

    // Handle both array of IDs and legacy string/other formats if necessary
    const sportIds = Array.isArray(athlete.sports) ? athlete.sports : [athlete.sports]

    // Look up sport names from config using the IDs
    const sportNames = sportIds.map((sportId) => {
      // If sportId is not a string/number (e.g. if it's already a name), handle gracefully
      // explicit check for id match
      const sport = sports.find((s) => s.id === sportId)
      return sport?.name || sportId || 'Unknown'
    })

    return sportNames.join(', ')
  }

  return (
    <>
      {showUserInfo && athlete && (
        <>
          <div className="px-3 py-2">
            <p className="text-sm font-semibold text-foreground">{athlete.name}</p>
            <p className="text-xs text-muted-foreground">{getSportDisplay()}</p>
          </div>
          <DropdownMenuSeparator className="bg-border" />
        </>
      )}

      <>
          {athleteMenuItems.map((item) => (
            <DropdownMenuItem
              key={item.path}
              onClick={() => navigate(item.path)}
              className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm text-foreground hover:bg-muted"
            >
              <item.icon className="h-4 w-4 text-muted-foreground" />
              <span>{item.label}</span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator className="bg-border" />

          <DropdownMenuItem
            onClick={() => navigate('/settings')}
            className="flex items-center gap-2 cursor-pointer rounded-lg px-3 py-2 text-sm text-foreground hover:bg-muted"
          >
            <Settings className="h-4 w-4 text-muted-foreground" />
            <span>Settings</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            onClick={() => navigate('/help')}
            className="flex items-center gap-2 cursor-pointer rounded-lg px-3 py-2 text-sm text-foreground hover:bg-muted"
          >
            <HelpCircle className="h-4 w-4 text-muted-foreground" />
            <span>Help</span>
          </DropdownMenuItem>
        </>

      <DropdownMenuItem
        onClick={handleLogout}
        className="flex items-center gap-2 cursor-pointer rounded-lg px-3 py-2 text-sm text-[#DC2626] hover:bg-muted focus:text-[#DC2626]"
      >
        <LogOut className="h-4 w-4" />
        <span>Sign Out</span>
      </DropdownMenuItem>
    </>
  )
}
